import React, { useState, useEffect } from 'react';
import { useTheme } from "../../context/ThemeContext";
import {
  Award,
  Plus,
  Search,
  Filter,
  Download,
  Upload,
  Users,
  Building2,
  Calendar,
  Percent,
  Edit,
  Trash2,
  CheckCircle2,
  XCircle,
  Mail,
  Phone,
  MapPin,
  MoreVertical,
  FileText,
  ExternalLink,
  Settings,
  Tag,
  User,
  Package,
  BookOpen,
  Gift,
  AlertTriangle,
  Ticket,
  Clock,
  Layers,
  Zap,
  BarChart,
} from 'lucide-react';

const ScholarshipsManager = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedConvention, setSelectedConvention] = useState(null);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showAwardModal, setShowAwardModal] = useState(false);
  const [showRuleModal, setShowRuleModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedAward, setSelectedAward] = useState(null);
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  
  // Mock data for demonstration
  const conventions = [
    { id: 1, name: "Jump Dance Convention" },
    { id: 2, name: "Velocity Dance Experience" },
    { id: 3, name: "Radix Dance Convention" },
  ];
  
  // Mock data for demonstration - enhanced for multi-tenant requirements
  const scholarshipTemplates = [
    {
      id: 1,
      conventionId: 1,
      name: "Elite Scholarship",
      description: "Full tuition scholarship for national events",
      color: "#4F46E5",
      awardSource: "manual",
      eligibleRecipient: "dancer",
      scholarshipType: "discount",
      discountValue: "100%",
      validityPeriod: "1 year",
      redemptionWindow: { type: "fixed", from: "2025-01-01", to: "2026-01-01" },
      eligibilityRules: {
        ageRange: { min: 8, max: 19 },
        allowedLevels: ["mini", "junior", "teen", "senior"],
        maxRedemptions: 1,
        onePerSeason: true,
        requiresStudio: true,
        transferable: false,
        stackable: false,
        fullRegistrantOnly: true
      },
      conflictBehavior: {
        multiplePresent: "highest",
        studioLock: "adminOverride"
      },
      associatedEvents: {
        awardedAt: ["regional"],
        redeemableAt: ["national"]
      },
      active: true
    },
    {
      id: 2,
      conventionId: 1,
      name: "Class Scholarship",
      description: "Partial scholarship for workshops",
      color: "#10B981",
      awardSource: "autoFaculty",
      eligibleRecipient: "dancer",
      scholarshipType: "discount",
      discountValue: "25%",
      validityPeriod: "Current season",
      redemptionWindow: { type: "months", value: 6 },
      eligibilityRules: {
        ageRange: { min: 8, max: 19 },
        allowedLevels: ["mini", "junior", "teen", "senior"],
        maxRedemptions: 3,
        onePerSeason: false,
        requiresStudio: true,
        transferable: false,
        stackable: true,
        fullRegistrantOnly: false
      },
      conflictBehavior: {
        multiplePresent: "choose",
        studioLock: "required"
      },
      associatedEvents: {
        awardedAt: ["regional"],
        redeemableAt: ["regional"]
      },
      active: true
    },
    {
      id: 3,
      conventionId: 2,
      name: "Studio Volume Discount",
      description: "Scholarship for studios with 10+ dancers",
      color: "#F59E0B",
      awardSource: "autoStudio",
      eligibleRecipient: "studio",
      scholarshipType: "discount",
      discountValue: "15%",
      validityPeriod: "Next event",
      redemptionWindow: { type: "months", value: 12 },
      eligibilityRules: {
        ageRange: null,
        allowedLevels: null,
        maxRedemptions: 1,
        onePerSeason: true,
        requiresStudio: true,
        transferable: false,
        stackable: false,
        fullRegistrantOnly: false
      },
      conflictBehavior: {
        multiplePresent: "highest",
        studioLock: "required"
      },
      associatedEvents: {
        awardedAt: ["regional"],
        redeemableAt: ["regional", "national"]
      },
      active: true
    }
  ];

  const awardedScholarships = [
    {
      id: 1,
      conventionId: 1,
      templateId: 1,
      dancerId: 101,
      dancerName: "Sarah Johnson",
      studioId: 201,
      studioName: "Dance Elite Academy",
      awardedDate: "2025-01-15",
      expiryDate: "2026-01-15",
      awardSource: "manual",
      awardedBy: "John Smith",
      status: "active",
      redemptionStatus: "unused",
      studioLocked: true,
      event: {
        type: "regional",
        id: 301,
        name: "Jump NYC 2025"
      }
    },
    {
      id: 2,
      conventionId: 1,
      templateId: 2,
      dancerId: 102,
      dancerName: "Michael Williams",
      studioId: 202,
      studioName: "Starlight Dance Company",
      awardedDate: "2024-11-20",
      expiryDate: "2025-05-20",
      awardSource: "autoFaculty",
      awardedBy: "Faculty Portal",
      status: "active",
      redemptionStatus: "unused",
      studioLocked: true,
      event: {
        type: "regional",
        id: 302,
        name: "Jump Boston 2024"
      }
    },
    {
      id: 3,
      conventionId: 2,
      templateId: 3,
      dancerId: null,
      dancerName: null,
      studioId: 203,
      studioName: "Premier Dance Studios",
      awardedDate: "2024-12-05",
      expiryDate: "2025-12-05",
      awardSource: "autoStudio",
      awardedBy: "System",
      status: "active",
      redemptionStatus: "unused",
      studioLocked: true,
      event: {
        type: "regional",
        id: 303,
        name: "Velocity Chicago 2024"
      }
    }
  ];
  
  // Filter templates and awards based on selected convention
  const filteredTemplates = selectedConvention 
    ? scholarshipTemplates.filter(template => template.conventionId === selectedConvention) 
    : scholarshipTemplates;
    
  const filteredAwards = selectedConvention 
    ? awardedScholarships.filter(award => award.conventionId === selectedConvention) 
    : awardedScholarships;

  useEffect(() => {
    // If conventions exist, select the first one by default
    if (conventions.length > 0 && !selectedConvention) {
      setSelectedConvention(conventions[0].id);
    }
  }, []);
  
  // Function to get scholarship template by ID
  const getTemplateById = (id) => {
    return scholarshipTemplates.find(template => template.id === id);
  };
  
  // Functions to handle modal interactions
  const openTemplateModal = (template = null) => {
    setSelectedTemplate(template);
    setShowTemplateModal(true);
  };
  
  const openAwardModal = (award = null) => {
    setSelectedAward(award);
    setShowAwardModal(true);
  };
  
  const openRuleModal = () => {
    setShowRuleModal(true);
  };
  
  // Placeholder statistics for the overview tab
  const stats = {
    active: 124,
    awarded: 45,
    expiringSoon: 12,
    expired: 8
  };
  
  // Maps for display labels
  const awardSourceMap = {
    manual: "Manual Award",
    autoFaculty: "Faculty Selection",
    autoTabulator: "Tabulator Rules",
    autoStudio: "Studio Volume"
  };
  
  const recipientTypeMap = {
    dancer: "Dancer",
    studio: "Studio",
    both: "Dancer & Studio"
  };
  
  const scholarshipTypeMap = {
    discount: "Discount",
    freeEntry: "Free Entry",
    eligibilityFlag: "Eligibility Flag"
  };

  // Scholarship Template Modal
  const ScholarshipTemplateModal = () => {
    const isEditing = selectedTemplate !== null;
    const isDarkMode = theme === 'dark';
    const initialData = isEditing ? selectedTemplate : {
      conventionId: selectedConvention,
      name: "",
      description: "",
      color: "#4F46E5",
      awardSource: "manual",
      eligibleRecipient: "dancer",
      scholarshipType: "discount",
      discountValue: "100%",
      validityPeriod: "1 year",
      redemptionWindow: { type: "fixed", from: "", to: "" },
      eligibilityRules: {
        ageRange: { min: 8, max: 19 },
        allowedLevels: ["mini", "junior", "teen", "senior"],
        maxRedemptions: 1,
        onePerSeason: true,
        requiresStudio: true,
        transferable: false,
        stackable: false,
        fullRegistrantOnly: true
      },
      conflictBehavior: {
        multiplePresent: "highest",
        studioLock: "adminOverride"
      },
      associatedEvents: {
        awardedAt: ["regional"],
        redeemableAt: ["national"]
      },
      active: true
    };
    
    const [formData, setFormData] = useState(initialData);
    const [activeSection, setActiveSection] = useState('basic');
    
    const handleChange = (e) => {
      const { name, value, type, checked } = e.target;
      
      if (type === 'checkbox') {
        setFormData(prev => ({
          ...prev,
          [name]: checked
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          [name]: value
        }));
      }
    };
    
    const handleNestedChange = (section, field, value) => {
      setFormData(prev => ({
        ...prev,
        [section]: {
          ...prev[section],
          [field]: value
        }
      }));
    };
    
    const handleSubmit = (e) => {
      e.preventDefault();
      // This would call your backend API to create/update the template
      console.log("Submitting template:", formData);
      setShowTemplateModal(false);
    };
    
    return (
      <div className="modal modal-open">
        <div className={`modal-box max-4xl ${isDarkMode ? 'bg-[#1a1f2d]/80 backdrop-blur-sm border border-white/5' : ''}`}>
          <h3 className="font-bold text-xl mb-6">
            {isEditing ? `Edit Template: ${selectedTemplate.name}` : "Create New Scholarship Template"}
          </h3>
          
          <div className="tabs tabs-boxed mb-4">
            <button 
              className={`tab ${activeSection === 'basic' ? 'tab-active' : ''}`}
              onClick={() => setActiveSection('basic')}
            >
              Basic Info
            </button>
            <button 
              className={`tab ${activeSection === 'eligibility' ? 'tab-active' : ''}`}
              onClick={() => setActiveSection('eligibility')}
            >
              Rules & Eligibility
            </button>
            <button 
              className={`tab ${activeSection === 'events' ? 'tab-active' : ''}`}
              onClick={() => setActiveSection('events')}
            >
              Events & Redemption
            </button>
            <button 
              className={`tab ${activeSection === 'conflicts' ? 'tab-active' : ''}`}
              onClick={() => setActiveSection('conflicts')}
            >
              Conflict Handling
            </button>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Info Section */}
            {activeSection === 'basic' && (
              <div className="space-y-4">
                <div className="form-control">
                  <label className="label">
                    <span className="label-text">Name</span>
                  </label>
                  <input 
                    type="text" 
                    name="name"
                    value={formData.name} 
                    onChange={handleChange}
                    className="input input-bordered" 
                    placeholder="e.g. Elite Scholarship" 
                    required 
                  />
                </div>
                
                <div className="form-control">
                  <label className="label">
                    <span className="label-text">Description</span>
                  </label>
                  <textarea 
                    name="description"
                    value={formData.description} 
                    onChange={handleChange}
                    className="textarea textarea-bordered" 
                    placeholder="Brief description of this scholarship type" 
                  />
                </div>
                
                <div className="form-control">
                  <label className="label">
                    <span className="label-text">Color Tag</span>
                  </label>
                  <input 
                    type="color" 
                    name="color"
                    value={formData.color} 
                    onChange={handleChange}
                    className="input input-bordered h-12 w-24" 
                  />
                </div>
                
                <div className="form-control">
                  <label className="label">
                    <span className="label-text">Award Source</span>
                  </label>
                  <select 
                    className="select select-bordered w-full" 
                    name="awardSource"
                    value={formData.awardSource} 
                    onChange={handleChange}
                  >
                    <option value="manual">Manual Award</option>
                    <option value="autoFaculty">Faculty Selection</option>
                    <option value="autoTabulator">Tabulator Rules</option>
                    <option value="autoStudio">Studio Volume</option>
                  </select>
                </div>
                
                <div className="form-control">
                  <label className="label">
                    <span className="label-text">Eligible Recipient</span>
                  </label>
                  <select 
                    className="select select-bordered w-full" 
                    name="eligibleRecipient"
                    value={formData.eligibleRecipient} 
                    onChange={handleChange}
                  >
                    <option value="dancer">Dancer</option>
                    <option value="studio">Studio</option>
                    <option value="both">Both Dancer & Studio</option>
                  </select>
                </div>
                
                <div className="form-control">
                  <label className="label">
                    <span className="label-text">Scholarship Type</span>
                  </label>
                  <select 
                    className="select select-bordered w-full" 
                    name="scholarshipType"
                    value={formData.scholarshipType} 
                    onChange={handleChange}
                  >
                    <option value="discount">Discount (% or $)</option>
                    <option value="freeEntry">Free Entry</option>
                    <option value="eligibilityFlag">Eligibility Flag</option>
                  </select>
                </div>
                
                <div className="form-control">
                  <label className="label">
                    <span className="label-text">Value</span>
                  </label>
                  <input 
                    type="text" 
                    name="discountValue"
                    value={formData.discountValue} 
                    onChange={handleChange}
                    className="input input-bordered" 
                    placeholder="e.g. 100% or $50" 
                  />
                </div>
              </div>
            )}
            
            {/* Eligibility Rules Section */}
            {activeSection === 'eligibility' && (
              <div className="space-y-4">
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="form-control flex-1">
                    <label className="label">
                      <span className="label-text">Minimum Age</span>
                    </label>
                    <input 
                      type="number" 
                      value={formData.eligibilityRules.ageRange.min} 
                      onChange={(e) => handleNestedChange('eligibilityRules', 'ageRange', {
                        ...formData.eligibilityRules.ageRange,
                        min: parseInt(e.target.value)
                      })}
                      className="input input-bordered" 
                      min="0"
                    />
                  </div>
                  
                  <div className="form-control flex-1">
                    <label className="label">
                      <span className="label-text">Maximum Age</span>
                    </label>
                    <input 
                      type="number" 
                      value={formData.eligibilityRules.ageRange.max} 
                      onChange={(e) => handleNestedChange('eligibilityRules', 'ageRange', {
                        ...formData.eligibilityRules.ageRange,
                        max: parseInt(e.target.value)
                      })}
                      className="input input-bordered" 
                      min="0"
                    />
                  </div>
                </div>
                
                <div className="form-control">
                  <label className="label">
                    <span className="label-text">Allowed Levels</span>
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {['mini', 'junior', 'teen', 'senior'].map(level => (
                      <label key={level} className="cursor-pointer label gap-2 bg-base-200 px-3 py-2 rounded-md">
                        <input 
                          type="checkbox" 
                          className="checkbox checkbox-sm" 
                          checked={formData.eligibilityRules.allowedLevels.includes(level)}
                          onChange={(e) => {
                            const updatedLevels = e.target.checked 
                              ? [...formData.eligibilityRules.allowedLevels, level]
                              : formData.eligibilityRules.allowedLevels.filter(l => l !== level);
                            
                            handleNestedChange('eligibilityRules', 'allowedLevels', updatedLevels);
                          }}
                        />
                        <span className="label-text capitalize">{level}</span>
                      </label>
                    ))}
                  </div>
                </div>
                
                <div className="form-control">
                  <label className="label">
                    <span className="label-text">Maximum Redemptions</span>
                  </label>
                  <input 
                    type="number" 
                    value={formData.eligibilityRules.maxRedemptions} 
                    onChange={(e) => handleNestedChange('eligibilityRules', 'maxRedemptions', 
                      parseInt(e.target.value)
                    )}
                    className="input input-bordered" 
                    min="1"
                  />
                </div>
                
                <div className="form-control">
                  <label className="label cursor-pointer justify-start gap-3">
                    <input 
                      type="checkbox" 
                      className="checkbox" 
                      checked={formData.eligibilityRules.onePerSeason}
                      onChange={(e) => handleNestedChange('eligibilityRules', 'onePerSeason', e.target.checked)}
                    />
                    <span className="label-text">Limit to one per season</span>
                  </label>
                </div>
                
                <div className="form-control">
                  <label className="label cursor-pointer justify-start gap-3">
                    <input 
                      type="checkbox" 
                      className="checkbox" 
                      checked={formData.eligibilityRules.requiresStudio}
                      onChange={(e) => handleNestedChange('eligibilityRules', 'requiresStudio', e.target.checked)}
                    />
                    <span className="label-text">Requires studio association</span>
                  </label>
                </div>
                
                <div className="form-control">
                  <label className="label cursor-pointer justify-start gap-3">
                    <input 
                      type="checkbox" 
                      className="checkbox" 
                      checked={formData.eligibilityRules.transferable}
                      onChange={(e) => handleNestedChange('eligibilityRules', 'transferable', e.target.checked)}
                    />
                    <span className="label-text">Transferable to another dancer</span>
                  </label>
                </div>
                
                <div className="form-control">
                  <label className="label cursor-pointer justify-start gap-3">
                    <input 
                      type="checkbox" 
                      className="checkbox" 
                      checked={formData.eligibilityRules.stackable}
                      onChange={(e) => handleNestedChange('eligibilityRules', 'stackable', e.target.checked)}
                    />
                    <span className="label-text">Stackable with other scholarships</span>
                  </label>
                </div>
                
                <div className="form-control">
                  <label className="label cursor-pointer justify-start gap-3">
                    <input 
                      type="checkbox" 
                      className="checkbox" 
                      checked={formData.eligibilityRules.fullRegistrantOnly}
                      onChange={(e) => handleNestedChange('eligibilityRules', 'fullRegistrantOnly', e.target.checked)}
                    />
                    <span className="label-text">Restrict to full registrants only</span>
                  </label>
                </div>
              </div>
            )}
            
            {/* Events & Redemption Section */}
            {activeSection === 'events' && (
              <div className="space-y-4">
                <div className="form-control">
                  <label className="label">
                    <span className="label-text">Events where award is given</span>
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {['regional', 'national', 'takeover'].map(eventType => (
                      <label key={eventType} className="cursor-pointer label gap-2 bg-base-200 px-3 py-2 rounded-md">
                        <input 
                          type="checkbox" 
                          className="checkbox checkbox-sm" 
                          checked={formData.associatedEvents.awardedAt.includes(eventType)}
                          onChange={(e) => {
                            const updatedTypes = e.target.checked 
                              ? [...formData.associatedEvents.awardedAt, eventType]
                              : formData.associatedEvents.awardedAt.filter(t => t !== eventType);
                            
                            handleNestedChange('associatedEvents', 'awardedAt', updatedTypes);
                          }}
                        />
                        <span className="label-text capitalize">{eventType}</span>
                      </label>
                    ))}
                  </div>
                </div>
                
                <div className="form-control">
                  <label className="label">
                    <span className="label-text">Events where can be redeemed</span>
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {['regional', 'national', 'takeover'].map(eventType => (
                      <label key={eventType} className="cursor-pointer label gap-2 bg-base-200 px-3 py-2 rounded-md">
                        <input 
                          type="checkbox" 
                          className="checkbox checkbox-sm" 
                          checked={formData.associatedEvents.redeemableAt.includes(eventType)}
                          onChange={(e) => {
                            const updatedTypes = e.target.checked 
                              ? [...formData.associatedEvents.redeemableAt, eventType]
                              : formData.associatedEvents.redeemableAt.filter(t => t !== eventType);
                            
                            handleNestedChange('associatedEvents', 'redeemableAt', updatedTypes);
                          }}
                        />
                        <span className="label-text capitalize">{eventType}</span>
                      </label>
                    ))}
                  </div>
                </div>
                
                <div className="form-control">
                  <label className="label">
                    <span className="label-text">Redemption Window Type</span>
                  </label>
                  <select 
                    className="select select-bordered w-full" 
                    value={formData.redemptionWindow.type}
                    onChange={(e) => handleNestedChange('redemptionWindow', 'type', e.target.value)}
                  >
                    <option value="fixed">Fixed Dates</option>
                    <option value="months">Valid for X Months</option>
                    <option value="nextEvent">Next Event Only</option>
                    <option value="nextSeason">Next Season Only</option>
                  </select>
                </div>
                
                {formData.redemptionWindow.type === 'fixed' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="form-control">
                      <label className="label">
                        <span className="label-text">Valid From</span>
                      </label>
                      <input 
                        type="date" 
                        value={formData.redemptionWindow.from} 
                        onChange={(e) => handleNestedChange('redemptionWindow', 'from', e.target.value)}
                        className="input input-bordered" 
                      />
                    </div>
                    
                    <div className="form-control">
                      <label className="label">
                        <span className="label-text">Valid To</span>
                      </label>
                      <input 
                        type="date" 
                        value={formData.redemptionWindow.to} 
                        onChange={(e) => handleNestedChange('redemptionWindow', 'to', e.target.value)}
                        className="input input-bordered" 
                      />
                    </div>
                  </div>
                )}
                
                {formData.redemptionWindow.type === 'months' && (
                  <div className="form-control">
                    <label className="label">
                      <span className="label-text">Valid For (Months)</span>
                    </label>
                    <input 
                      type="number" 
                      value={formData.redemptionWindow.value || ''} 
                      onChange={(e) => handleNestedChange('redemptionWindow', 'value', parseInt(e.target.value))}
                      className="input input-bordered" 
                      min="1"
                    />
                  </div>
                )}
              </div>
            )}
            
            {/* Conflict Handling Section */}
            {activeSection === 'conflicts' && (
              <div className="space-y-4">
                <div className="form-control">
                  <label className="label">
                    <span className="label-text">If multiple scholarships present</span>
                  </label>
                  <select 
                    className="select select-bordered w-full" 
                    value={formData.conflictBehavior.multiplePresent}
                    onChange={(e) => handleNestedChange('conflictBehavior', 'multiplePresent', e.target.value)}
                  >
                    <option value="highest">Apply highest value</option>
                    <option value="choose">Let user choose</option>
                    <option value="first">Use first applicable</option>
                  </select>
                </div>
                
                <div className="form-control">
                  <label className="label">
                    <span className="label-text">If studio locks are active</span>
                  </label>
                  <select 
                    className="select select-bordered w-full" 
                    value={formData.conflictBehavior.studioLock}
                    onChange={(e) => handleNestedChange('conflictBehavior', 'studioLock', e.target.value)}
                  >
                    <option value="required">Studio must approve release</option>
                    <option value="adminOverride">Admin overrides only</option>
                    <option value="autoAllowed">Auto-allowed if no active registration</option>
                  </select>
                </div>
              </div>
            )}
            
            <div className="modal-action flex justify-between">
              <button 
                type="button" 
                className="btn btn-ghost" 
                onClick={() => setShowTemplateModal(false)}
              >
                Cancel
              </button>
              <button type="submit" className="btn btn-primary">
                {isEditing ? "Update Template" : "Create Template"}
              </button>
            </div>
          </form>
        </div>
        <div className="modal-backdrop" onClick={() => setShowTemplateModal(false)}></div>
      </div>
    );
  };

  // Award Scholarship Modal
  const AwardScholarshipModal = () => {
    const isEditing = selectedAward !== null;
    const isDarkMode = theme === 'dark';
    const initialData = isEditing ? selectedAward : {
      templateId: '',
      dancerId: '',
      dancerName: '',
      studioId: '',
      studioName: '',
      eventId: '',
      eventName: '',
      notifyRecipient: true,
      notes: ''
    };
    
    const [formData, setFormData] = useState(initialData);
    
    // Mock data for dropdowns
    const availableTemplates = filteredTemplates.filter(t => t.awardSource === 'manual');
    const mockDancers = [
      { id: 101, name: "Sarah Johnson" },
      { id: 102, name: "Michael Williams" },
      { id: 103, name: "Emma Thompson" }
    ];
    const mockStudios = [
      { id: 201, name: "Dance Elite Academy" },
      { id: 202, name: "Starlight Dance Company" },
      { id: 203, name: "Premier Dance Studios" }
    ];
    const mockEvents = [
      { id: 301, name: "Jump NYC 2025", type: "regional" },
      { id: 302, name: "Jump Nationals 2025", type: "national" },
      { id: 303, name: "Jump LA Takeover", type: "takeover" }
    ];
    
    const handleChange = (e) => {
      const { name, value, type, checked } = e.target;
      
      if (type === 'checkbox') {
        setFormData(prev => ({
          ...prev,
          [name]: checked
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          [name]: value
        }));
      }
    };
    
    const handleDancerSelect = (id) => {
      const dancer = mockDancers.find(d => d.id === parseInt(id));
      setFormData(prev => ({
        ...prev,
        dancerId: id,
        dancerName: dancer ? dancer.name : ''
      }));
    };
    
    const handleStudioSelect = (id) => {
      const studio = mockStudios.find(s => s.id === parseInt(id));
      setFormData(prev => ({
        ...prev,
        studioId: id,
        studioName: studio ? studio.name : ''
      }));
    };
    
    const handleEventSelect = (id) => {
      const event = mockEvents.find(e => e.id === parseInt(id));
      setFormData(prev => ({
        ...prev,
        eventId: id,
        eventName: event ? event.name : ''
      }));
    };
    
    const handleSubmit = (e) => {
      e.preventDefault();
      // This would call your backend API to create/update the award
      console.log("Submitting award:", formData);
      setShowAwardModal(false);
    };
    
    // Get the selected template details
    const selectedTemplateDetails = formData.templateId ? 
      availableTemplates.find(t => t.id === parseInt(formData.templateId)) : null;
    
    return (
      <div className="modal modal-open">
        <div className={`modal-box max-2xl ${isDarkMode ? 'bg-[#1a1f2d]/80 backdrop-blur-sm border border-white/5' : ''}`}>
          <h3 className="font-bold text-xl mb-6">
            {isEditing ? "Edit Scholarship Award" : "Award New Scholarship"}
          </h3>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="form-control">
              <label className="label">
                <span className="label-text">Scholarship Template</span>
              </label>
              <select 
                className="select select-bordered w-full" 
                name="templateId"
                value={formData.templateId} 
                onChange={handleChange}
                required
              >
                <option value="">Select a scholarship template</option>
                {availableTemplates.map(template => (
                  <option key={template.id} value={template.id}>
                    {template.name} ({template.discountValue})
                  </option>
                ))}
              </select>
            </div>
            
            {selectedTemplateDetails && (
              <div className="bg-base-200 p-4 rounded-lg">
                <h4 className="font-semibold">{selectedTemplateDetails.name}</h4>
                <p className="text-sm">{selectedTemplateDetails.description}</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  <div className="badge" style={{backgroundColor: selectedTemplateDetails.color, color: '#fff'}}>
                    {selectedTemplateDetails.discountValue}
                  </div>
                  <div className="badge badge-outline">
                    {recipientTypeMap[selectedTemplateDetails.eligibleRecipient]}
                  </div>
                </div>
              </div>
            )}
            
            {selectedTemplateDetails?.eligibleRecipient !== 'studio' && (
              <div className="form-control">
                <label className="label">
                  <span className="label-text">Dancer</span>
                </label>
                <select 
                  className="select select-bordered w-full" 
                  name="dancerId"
                  value={formData.dancerId} 
                  onChange={(e) => handleDancerSelect(e.target.value)}
                  required={selectedTemplateDetails?.eligibleRecipient !== 'studio'}
                >
                  <option value="">Select a dancer</option>
                  {mockDancers.map(dancer => (
                    <option key={dancer.id} value={dancer.id}>
                      {dancer.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            
            {selectedTemplateDetails?.eligibleRecipient !== 'dancer' && (
              <div className="form-control">
                <label className="label">
                  <span className="label-text">Studio</span>
                </label>
                <select 
                  className="select select-bordered w-full" 
                  name="studioId"
                  value={formData.studioId} 
                  onChange={(e) => handleStudioSelect(e.target.value)}
                  required={selectedTemplateDetails?.eligibleRecipient !== 'dancer'}
                >
                  <option value="">Select a studio</option>
                  {mockStudios.map(studio => (
                    <option key={studio.id} value={studio.id}>
                      {studio.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            
            <div className="form-control">
              <label className="label">
                <span className="label-text">Event</span>
              </label>
              <select 
                className="select select-bordered w-full" 
                name="eventId"
                value={formData.eventId} 
                onChange={(e) => handleEventSelect(e.target.value)}
                required
              >
                <option value="">Select event where awarded</option>
                {mockEvents.map(event => (
                  <option key={event.id} value={event.id}>
                    {event.name}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="form-control">
              <label className="label">
                <span className="label-text">Notes</span>
              </label>
              <textarea 
                name="notes"
                value={formData.notes} 
                onChange={handleChange}
                className="textarea textarea-bordered" 
                placeholder="Optional notes about this award" 
              />
            </div>
            
            <div className="form-control">
              <label className="label cursor-pointer justify-start gap-3">
                <input 
                  type="checkbox" 
                  name="notifyRecipient"
                  className="checkbox" 
                  checked={formData.notifyRecipient}
                  onChange={handleChange}
                />
                <span className="label-text">Notify recipient by email</span>
              </label>
            </div>
            
            <div className="modal-action flex justify-between">
              <button 
                type="button" 
                className="btn btn-ghost" 
                onClick={() => setShowAwardModal(false)}
              >
                Cancel
              </button>
              <button type="submit" className="btn btn-primary">
                {isEditing ? "Update Award" : "Award Scholarship"}
              </button>
            </div>
          </form>
        </div>
        <div className="modal-backdrop" onClick={() => setShowAwardModal(false)}></div>
      </div>
    );
  };

  // Rules & Settings Modal
  const RulesSettingsModal = () => {
    const { theme } = useTheme();
    const isDarkMode = theme === 'dark';
    const initialSettings = {
      studioBinding: {
        enabled: true,
        overridePolicy: 'adminOnly', // 'adminOnly', 'studioApproval', 'dancerRequest'
      },
      conflictResolution: {
        stackingPolicy: 'noStacking', // 'noStacking', 'allowStacking', 'perTemplateRules'
        multiplePolicy: 'highestValue', // 'highestValue', 'userChoice', 'firstApplicable'
      },
      notifications: {
        notifyOnAward: true,
        notifyOnExpiry: true,
        reminderDays: 30,
      },
      redemptionLimits: {
        perDancer: 3,
        perStudio: 5,
        perSeason: true,
      }
    };
    
    const [settings, setSettings] = useState(initialSettings);
    
    const handleChange = (section, field, value) => {
      setSettings(prev => ({
        ...prev,
        [section]: {
          ...prev[section],
          [field]: value
        }
      }));
    };
    
    const handleSubmit = (e) => {
      e.preventDefault();
      // This would call your backend API to update settings
      console.log("Updating settings:", settings);
      setShowRuleModal(false);
    };
    
    return (
      <div className="modal modal-open">
        <div className={`modal-box max-w-3xl ${isDarkMode ? 'bg-[#1a1f2d]/80 backdrop-blur-sm border border-white/5' : ''}`}>
          <h3 className="font-bold text-xl mb-6">Scholarship Rules & Settings</h3>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className={`card shadow-md ${isDarkMode ? 'bg-[#131620] border border-white/5' : 'bg-base-100'}`}>
              <div className="card-body">
                <h2 className="card-title">
                  <Building2 className="w-5 h-5 mr-2" />
                  Studio Binding Rules
                </h2>
                
                <div className="form-control">
                  <label className="label cursor-pointer justify-start gap-3">
                    <input 
                      type="checkbox" 
                      className="toggle toggle-primary" 
                      checked={settings.studioBinding.enabled}
                      onChange={(e) => handleChange('studioBinding', 'enabled', e.target.checked)}
                    />
                    <span className="label-text font-medium">Enable Studio Binding</span>
                  </label>
                  <span className="text-sm text-base-content/70 ml-10">
                    When enabled, scholarships are only redeemable with the studio associated at the time of award
                  </span>
                </div>
                
                <div className="form-control mt-4">
                  <label className="label">
                    <span className="label-text">Studio Lock Override Policy</span>
                  </label>
                  <select 
                    className="select select-bordered w-full" 
                    value={settings.studioBinding.overridePolicy}
                    onChange={(e) => handleChange('studioBinding', 'overridePolicy', e.target.value)}
                    disabled={!settings.studioBinding.enabled}
                  >
                    <option value="adminOnly">Admin Override Only</option>
                    <option value="studioApproval">Studio Must Approve Release</option>
                    <option value="dancerRequest">Dancer Can Request Release</option>
                  </select>
                </div>
              </div>
            </div>
            
            <div className={`card shadow-md ${isDarkMode ? 'bg-[#131620] border border-white/5' : 'bg-base-100'}`}>
              <div className="card-body">
                <h2 className="card-title">
                  <AlertTriangle className="w-5 h-5 mr-2" />
                  Conflict Resolution
                </h2>
                
                <div className="form-control">
                  <label className="label">
                    <span className="label-text">Scholarship Stacking Policy</span>
                  </label>
                  <select 
                    className="select select-bordered w-full" 
                    value={settings.conflictResolution.stackingPolicy}
                    onChange={(e) => handleChange('conflictResolution', 'stackingPolicy', e.target.value)}
                  >
                    <option value="noStacking">No Stacking Allowed</option>
                    <option value="allowStacking">Allow Stacking</option>
                    <option value="perTemplateRules">Use Per-Template Rules</option>
                  </select>
                </div>
                
                <div className="form-control mt-4">
                  <label className="label">
                    <span className="label-text">Multiple Scholarships Policy</span>
                  </label>
                  <select 
                    className="select select-bordered w-full" 
                    value={settings.conflictResolution.multiplePolicy}
                    onChange={(e) => handleChange('conflictResolution', 'multiplePolicy', e.target.value)}
                  >
                    <option value="highestValue">Apply Highest Value</option>
                    <option value="userChoice">Let User Choose</option>
                    <option value="firstApplicable">Use First Applicable</option>
                  </select>
                </div>
              </div>
            </div>
            
            <div className={`card shadow-md ${isDarkMode ? 'bg-[#131620] border border-white/5' : 'bg-base-100'}`}>
              <div className="card-body">
                <h2 className="card-title">
                  <Mail className="w-5 h-5 mr-2" />
                  Notifications
                </h2>
                
                <div className="form-control">
                  <label className="label cursor-pointer justify-start gap-3">
                    <input 
                      type="checkbox" 
                      className="checkbox" 
                      checked={settings.notifications.notifyOnAward}
                      onChange={(e) => handleChange('notifications', 'notifyOnAward', e.target.checked)}
                    />
                    <span className="label-text">Notify recipients when awarded</span>
                  </label>
                </div>
                
                <div className="form-control">
                  <label className="label cursor-pointer justify-start gap-3">
                    <input 
                      type="checkbox" 
                      className="checkbox" 
                      checked={settings.notifications.notifyOnExpiry}
                      onChange={(e) => handleChange('notifications', 'notifyOnExpiry', e.target.checked)}
                    />
                    <span className="label-text">Notify when scholarship is expiring</span>
                  </label>
                </div>
                
                <div className="form-control mt-2">
                  <label className="label">
                    <span className="label-text">Send expiry reminder days before</span>
                  </label>
                  <input 
                    type="number" 
                    value={settings.notifications.reminderDays} 
                    onChange={(e) => handleChange('notifications', 'reminderDays', parseInt(e.target.value))}
                    className="input input-bordered w-full" 
                    min="1"
                    disabled={!settings.notifications.notifyOnExpiry}
                  />
                </div>
              </div>
            </div>
            
            <div className={`card shadow-md ${isDarkMode ? 'bg-[#131620] border border-white/5' : 'bg-base-100'}`}>
              <div className="card-body">
                <h2 className="card-title">
                  <Settings className="w-5 h-5 mr-2" />
                  Redemption Limits
                </h2>
                
                <div className="form-control">
                  <label className="label">
                    <span className="label-text">Maximum scholarships per dancer</span>
                  </label>
                  <input 
                    type="number" 
                    value={settings.redemptionLimits.perDancer} 
                    onChange={(e) => handleChange('redemptionLimits', 'perDancer', parseInt(e.target.value))}
                    className="input input-bordered w-full" 
                    min="1"
                  />
                </div>
                
                <div className="form-control mt-2">
                  <label className="label">
                    <span className="label-text">Maximum scholarships per studio</span>
                  </label>
                  <input 
                    type="number" 
                    value={settings.redemptionLimits.perStudio} 
                    onChange={(e) => handleChange('redemptionLimits', 'perStudio', parseInt(e.target.value))}
                    className="input input-bordered w-full" 
                    min="1"
                  />
                </div>
                
                <div className="form-control mt-2">
                  <label className="label cursor-pointer justify-start gap-3">
                    <input 
                      type="checkbox" 
                      className="checkbox" 
                      checked={settings.redemptionLimits.perSeason}
                      onChange={(e) => handleChange('redemptionLimits', 'perSeason', e.target.checked)}
                    />
                    <span className="label-text">Reset limits each season</span>
                  </label>
                </div>
              </div>
            </div>
            
            <div className="modal-action flex justify-between">
              <button 
                type="button" 
                className="btn btn-ghost" 
                onClick={() => setShowRuleModal(false)}
              >
                Cancel
              </button>
              <button type="submit" className="btn btn-primary">
                Save Settings
              </button>
            </div>
          </form>
        </div>
        <div className="modal-backdrop" onClick={() => setShowRuleModal(false)}></div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Convention Selector */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Scholarships Manager</h1>
          <p className="text-base-content/70 mt-1">Manage and track scholarship programs</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="form-control w-64">
            <select 
              className="select select-bordered w-full" 
              value={selectedConvention || ''}
              onChange={(e) => setSelectedConvention(parseInt(e.target.value))}
            >
              <option value="">All Conventions</option>
              {conventions.map(convention => (
                <option key={convention.id} value={convention.id}>
                  {convention.name}
                </option>
              ))}
            </select>
          </div>
          <button className="btn btn-primary" onClick={() => openTemplateModal()}>
            <Plus className="w-4 h-4 mr-2" />
            Create Template
          </button>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className={`stat rounded-box ${
          isDarkMode ? 'bg-[#1a1f2d]/80 backdrop-blur-sm border border-white/5 shadow-xl' : 'bg-base-100'
        }`}>
          <div className="stat-figure text-primary">
            <Award className="w-8 h-8" />
          </div>
          <div className="stat-title">Active Scholarships</div>
          <div className="stat-value text-primary">{stats.active}</div>
          <div className="stat-desc">↗︎ 14 (30 days)</div>
        </div>
        <div className={`stat rounded-box ${
          isDarkMode ? 'bg-[#1a1f2d]/80 backdrop-blur-sm border border-white/5 shadow-xl' : 'bg-base-100'
        }`}>
          <div className="stat-figure text-success">
            <CheckCircle2 className="w-8 h-8" />
          </div>
          <div className="stat-title">Awarded This Month</div>
          <div className="stat-value text-success">{stats.awarded}</div>
          <div className="stat-desc">↗︎ 8 from last month</div>
        </div>
        <div className={`stat rounded-box ${
          isDarkMode ? 'bg-[#1a1f2d]/80 backdrop-blur-sm border border-white/5 shadow-xl' : 'bg-base-100'
        }`}>
          <div className="stat-figure text-warning">
            <Calendar className="w-8 h-8" />
          </div>
          <div className="stat-title">Expiring Soon</div>
          <div className="stat-value text-warning">{stats.expiringSoon}</div>
          <div className="stat-desc">Next 30 days</div>
        </div>
        <div className={`stat rounded-box ${
          isDarkMode ? 'bg-[#1a1f2d]/80 backdrop-blur-sm border border-white/5 shadow-xl' : 'bg-base-100'
        }`}>
          <div className="stat-figure text-error">
            <XCircle className="w-8 h-8" />
          </div>
          <div className="stat-title">Expired</div>
          <div className="stat-value text-error">{stats.expired}</div>
          <div className="stat-desc">This month</div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className={`tabs tabs-boxed ${isDarkMode ? 'bg-[#1a1f2d]/80 backdrop-blur-sm border border-white/5' : 'bg-base-100'}`}>
        <button 
          className={`tab ${activeTab === 'overview' ? 'tab-active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button 
          className={`tab ${activeTab === 'types' ? 'tab-active' : ''}`}
          onClick={() => setActiveTab('types')}
        >
          Templates
        </button>
        <button 
          className={`tab ${activeTab === 'awards' ? 'tab-active' : ''}`}
          onClick={() => setActiveTab('awards')}
        >
          Awards
        </button>
        <button 
          className={`tab ${activeTab === 'rules' ? 'tab-active' : ''}`}
          onClick={() => setActiveTab('rules')}
        >
          Rules & Settings
        </button>
        <button 
          className={`tab ${activeTab === 'analytics' ? 'tab-active' : ''}`}
          onClick={() => setActiveTab('analytics')}
        >
          Analytics
        </button>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-base-content/50" />
          <input
            type="text"
            placeholder="Search scholarships, dancers, or studios..."
            className="input input-bordered w-full pl-10"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          <button className="btn btn-outline gap-2">
            <Filter className="w-4 h-4" />
            Filters
          </button>
          <button className="btn btn-outline gap-2">
            <Download className="w-4 h-4" />
            Export
          </button>
          {activeTab === 'awards' && (
            <button className="btn btn-primary gap-2" onClick={() => openAwardModal()}>
              <Plus className="w-4 h-4" />
              Award
            </button>
          )}
          {activeTab === 'rules' && (
            <button className="btn btn-primary gap-2" onClick={() => openRuleModal()}>
              <Settings className="w-4 h-4" />
              Edit Settings
            </button>
          )}
        </div>
      </div>

      {/* Templates Tab Content */}
      {activeTab === 'types' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Create Template Card */}
          <div className={`card border-2 border-dashed border-base-content/20 flex items-center justify-center cursor-pointer h-64 ${
            isDarkMode ? 'bg-[#131620]' : 'bg-base-200 shadow-lg'
          }`}
            onClick={() => openTemplateModal()}>
            <div className="card-body items-center text-center">
              <Plus className="w-12 h-12 text-base-content/50" />
              <h3 className="card-title mt-2">Create New Template</h3>
              <p className="text-base-content/70">Add a new scholarship type</p>
            </div>
          </div>
          
          {/* Existing Templates */}
          {filteredTemplates.map(template => (
            <div key={template.id} 
                className={`card hover:shadow-xl transition-shadow cursor-pointer ${
                  isDarkMode 
                    ? 'bg-[#1a1f2d]/80 backdrop-blur-sm border border-white/5 shadow-lg' 
                    : 'bg-base-100 shadow-lg'
                }`}
                onClick={() => openTemplateModal(template)}>
              <div className="card-body">
                <div className="flex justify-between items-start">
                  <h3 className="card-title">{template.name}</h3>
                  <div className="badge" style={{backgroundColor: template.color, color: '#fff'}}>
                    {template.discountValue}
                  </div>
                </div>
                <p className="text-sm text-base-content/70">{template.description}</p>
                
                <div className="flex gap-2 mt-2 flex-wrap">
                  <div className="badge badge-outline">
                    {awardSourceMap[template.awardSource]}
                  </div>
                  <div className="badge badge-outline">
                    {recipientTypeMap[template.eligibleRecipient]}
                  </div>
                </div>
                
                <div className="divider my-2"></div>
                
                <div className="space-y-2">
                  <h4 className="font-semibold text-sm">Key Rules:</h4>
                  <ul className="text-xs text-base-content/70 space-y-1">
                    <li className="flex items-center gap-2">
                      <Clock className="w-3 h-3" />
                      Valid: {template.validityPeriod}
                    </li>
                    
                    {template.eligibilityRules.ageRange && (
                      <li className="flex items-center gap-2">
                        <User className="w-3 h-3" />
                        Ages: {template.eligibilityRules.ageRange.min}-{template.eligibilityRules.ageRange.max}
                      </li>
                    )}
                    
                    <li className="flex items-center gap-2">
                      <Layers className="w-3 h-3" />
                      {template.eligibilityRules.stackable ? "Stackable" : "Non-stackable"}
                    </li>
                  </ul>
                </div>

                <div className="card-actions justify-end mt-4">
                  <button 
                    className="btn btn-ghost btn-sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      openTemplateModal(template);
                    }}
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Awards Tab Content */}
      {activeTab === 'awards' && (
        <>
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>Recipient</th>
                  <th>Scholarship</th>
                  <th>Event</th>
                  <th>Awarded</th>
                  <th>Expiry</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredAwards.map(award => (
                  <tr key={award.id} className="hover cursor-pointer" onClick={() => openAwardModal(award)}>
                    <td>
                      {award.dancerName ? (
                        <div className="flex flex-col">
                          <span>{award.dancerName}</span>
                          <span className="text-xs text-base-content/70">{award.studioName}</span>
                        </div>
                      ) : (
                        <span>{award.studioName}</span>
                      )}
                    </td>
                    <td>
                      {getTemplateById(award.templateId)?.name || "Unknown Template"}
                    </td>
                    <td>
                      <div className="flex flex-col">
                        <span>{award.event.name}</span>
                        <span className="text-xs text-base-content/70 capitalize">{award.event.type}</span>
                      </div>
                    </td>
                    <td>{new Date(award.awardedDate).toLocaleDateString()}</td>
                    <td>{new Date(award.expiryDate).toLocaleDateString()}</td>
                    <td>
                      {award.status === 'active' && (
                        <div className="badge badge-success">Active</div>
                      )}
                      {award.status === 'expired' && (
                        <div className="badge badge-error">Expired</div>
                      )}
                      {award.status === 'redeemed' && (
                        <div className="badge badge-info">Redeemed</div>
                      )}
                    </td>
                    <td>
                      <div className="flex gap-2">
                        <button 
                          className="btn btn-ghost btn-sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            openAwardModal(award);
                          }}
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button 
                          className="btn btn-ghost btn-sm text-error"
                          onClick={(e) => {
                            e.stopPropagation();
                            // Would show a confirmation dialog
                          }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {filteredAwards.length === 0 && (
            <div className="text-center py-8">
              <Award className="w-12 h-12 mx-auto text-base-content/30" />
              <h3 className="mt-4 text-lg font-medium">No scholarships awarded yet</h3>
              <p className="text-base-content/70 mt-1">
                Start by creating a scholarship template and then award it to dancers or studios
              </p>
              <button className="btn btn-primary mt-4" onClick={() => openAwardModal()}>
                <Plus className="w-4 h-4 mr-2" />
                Award Scholarship
              </button>
            </div>
          )}
        </>
      )}
      
      {/* Rules & Settings Tab Content */}
      {activeTab === 'rules' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className={`card shadow-md ${
            isDarkMode ? 'bg-[#1a1f2d]/80 backdrop-blur-sm border border-white/5' : 'bg-base-100'
          }`}>
            <div className="card-body">
              <h2 className="card-title text-lg flex items-center">
                <Building2 className="w-5 h-5 mr-2" />
                Studio Binding
              </h2>
              <p className="text-base-content/70 text-sm">
                Control how scholarships are tied to studios and how dancers can use them
              </p>
              
              <div className="mt-4 space-y-2">
                <div className="flex justify-between items-center">
                  <span>Studio binding enabled</span>
                  <div className="badge badge-success">Enabled</div>
                </div>
                <div className="flex justify-between items-center">
                  <span>Override policy</span>
                  <div className="badge">Admin Only</div>
                </div>
              </div>
              
              <div className="card-actions justify-end mt-4">
                <button className="btn btn-primary btn-sm" onClick={() => openRuleModal()}>
                  Edit Settings
                </button>
              </div>
            </div>
          </div>
          
          <div className={`card shadow-md ${
            isDarkMode ? 'bg-[#1a1f2d]/80 backdrop-blur-sm border border-white/5' : 'bg-base-100'
          }`}>
            <div className="card-body">
              <h2 className="card-title text-lg flex items-center">
                <AlertTriangle className="w-5 h-5 mr-2" />
                Conflict Resolution
              </h2>
              <p className="text-base-content/70 text-sm">
                Define behavior when multiple scholarships could apply
              </p>
              
              <div className="mt-4 space-y-2">
                <div className="flex justify-between items-center">
                  <span>Stacking policy</span>
                  <div className="badge">Per Template</div>
                </div>
                <div className="flex justify-between items-center">
                  <span>Multiple scholarships</span>
                  <div className="badge">Highest Value</div>
                </div>
              </div>
              
              <div className="card-actions justify-end mt-4">
                <button className="btn btn-primary btn-sm" onClick={() => openRuleModal()}>
                  Edit Settings
                </button>
              </div>
            </div>
          </div>
          
          <div className={`card shadow-md ${
            isDarkMode ? 'bg-[#1a1f2d]/80 backdrop-blur-sm border border-white/5' : 'bg-base-100'
          }`}>
            <div className="card-body">
              <h2 className="card-title text-lg flex items-center">
                <Mail className="w-5 h-5 mr-2" />
                Notifications
              </h2>
              <p className="text-base-content/70 text-sm">
                Configure when and how to notify recipients about scholarships
              </p>
              
              <div className="mt-4 space-y-2">
                <div className="flex justify-between items-center">
                  <span>Notify on award</span>
                  <div className="badge badge-success">Enabled</div>
                </div>
                <div className="flex justify-between items-center">
                  <span>Expiry reminders</span>
                  <div className="badge badge-success">30 days before</div>
                </div>
              </div>
              
              <div className="card-actions justify-end mt-4">
                <button className="btn btn-primary btn-sm" onClick={() => openRuleModal()}>
                  Edit Settings
                </button>
              </div>
            </div>
          </div>
          
          <div className={`card shadow-md ${
            isDarkMode ? 'bg-[#1a1f2d]/80 backdrop-blur-sm border border-white/5' : 'bg-base-100'
          }`}>
            <div className="card-body">
              <h2 className="card-title text-lg flex items-center">
                <Ticket className="w-5 h-5 mr-2" />
                Redemption Limits
              </h2>
              <p className="text-base-content/70 text-sm">
                Set limits on how many scholarships can be awarded
              </p>
              
              <div className="mt-4 space-y-2">
                <div className="flex justify-between items-center">
                  <span>Per dancer</span>
                  <div className="badge">3 max</div>
                </div>
                <div className="flex justify-between items-center">
                  <span>Per studio</span>
                  <div className="badge">5 max</div>
                </div>
                <div className="flex justify-between items-center">
                  <span>Reset each season</span>
                  <div className="badge badge-success">Yes</div>
                </div>
              </div>
              
              <div className="card-actions justify-end mt-4">
                <button className="btn btn-primary btn-sm" onClick={() => openRuleModal()}>
                  Edit Settings
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Analytics Tab Content */}
      {activeTab === 'analytics' && (
        <div className="space-y-6">
          <div className="text-center py-8">
            <BarChart className="w-12 h-12 mx-auto text-base-content/30" />
            <h3 className="mt-4 text-lg font-medium">Analytics Dashboard</h3>
            <p className="text-base-content/70 mt-1">
              This feature will be available soon
            </p>
          </div>
        </div>
      )}
      
      {/* Overview Tab Content */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Recent Awards */}
            <div className={`card lg:col-span-2 ${
              isDarkMode ? 'bg-[#1a1f2d]/80 backdrop-blur-sm border border-white/5 shadow-xl' : 'bg-base-100 shadow-md'
            }`}>
              <div className="card-body">
                <h2 className="card-title text-lg">Recent Awards</h2>
                
                <div className="overflow-x-auto mt-4">
                  <table className="table table-sm">
                    <thead>
                      <tr>
                        <th>Recipient</th>
                        <th>Scholarship</th>
                        <th>Awarded</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredAwards.slice(0, 5).map(award => (
                        <tr key={award.id}>
                          <td>
                            {award.dancerName || award.studioName}
                          </td>
                          <td>
                            {getTemplateById(award.templateId)?.name || "Unknown Template"}
                          </td>
                          <td>{new Date(award.awardedDate).toLocaleDateString()}</td>
                          <td>
                            {award.status === 'active' && (
                              <div className="badge badge-sm badge-success">Active</div>
                            )}
                            {award.status === 'expired' && (
                              <div className="badge badge-sm badge-error">Expired</div>
                            )}
                            {award.status === 'redeemed' && (
                              <div className="badge badge-sm badge-info">Redeemed</div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                <div className="card-actions justify-end mt-4">
                  <button className="btn btn-ghost btn-sm" onClick={() => setActiveTab('awards')}>
                    View All
                  </button>
                </div>
              </div>
            </div>
            
            {/* Quick Actions */}
            <div className={`card ${
              isDarkMode ? 'bg-[#1a1f2d]/80 backdrop-blur-sm border border-white/5 shadow-xl' : 'bg-base-100 shadow-md'
            }`}>
              <div className="card-body">
                <h2 className="card-title text-lg">Quick Actions</h2>
                
                <div className="space-y-3 mt-4">
                  <button className="btn btn-outline w-full justify-start" onClick={() => openTemplateModal()}>
                    <Plus className="w-4 h-4 mr-2" />
                    Create Scholarship Template
                  </button>
                  
                  <button className="btn btn-outline w-full justify-start" onClick={() => openAwardModal()}>
                    <Gift className="w-4 h-4 mr-2" />
                    Award Scholarship
                  </button>
                  
                  <button className="btn btn-outline w-full justify-start" onClick={() => openRuleModal()}>
                    <Settings className="w-4 h-4 mr-2" />
                    Edit Rules & Settings
                  </button>
                  
                  <button className="btn btn-outline w-full justify-start">
                    <Download className="w-4 h-4 mr-2" />
                    Export Scholarship Data
                  </button>
                </div>
              </div>
            </div>
            
            {/* Active Templates */}
            <div className={`card lg:col-span-3 ${
              isDarkMode ? 'bg-[#1a1f2d]/80 backdrop-blur-sm border border-white/5 shadow-xl' : 'bg-base-100 shadow-md'
            }`}>
              <div className="card-body">
                <h2 className="card-title text-lg">Active Scholarship Templates</h2>
                
                <div className="overflow-x-auto mt-4">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Template</th>
                        <th>Award Source</th>
                        <th>Recipient</th>
                        <th>Type</th>
                        <th>Value</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredTemplates.slice(0, 5).map(template => (
                        <tr key={template.id}>
                          <td>
                            <div className="flex items-center gap-2">
                              <div className="w-3 h-3 rounded-full" style={{backgroundColor: template.color}}></div>
                              {template.name}
                            </div>
                          </td>
                          <td>{awardSourceMap[template.awardSource]}</td>
                          <td>{recipientTypeMap[template.eligibleRecipient]}</td>
                          <td>{scholarshipTypeMap[template.scholarshipType]}</td>
                          <td>{template.discountValue}</td>
                          <td>
                            {template.active ? (
                              <div className="badge badge-success">Active</div>
                            ) : (
                              <div className="badge badge-error">Inactive</div>
                            )}
                          </td>
                          <td>
                            <div className="flex gap-2">
                              <button className="btn btn-ghost btn-sm" onClick={() => openTemplateModal(template)}>
                                <Edit className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                <div className="card-actions justify-end mt-4">
                  <button className="btn btn-ghost btn-sm" onClick={() => setActiveTab('types')}>
                    View All
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      {showTemplateModal && <ScholarshipTemplateModal />}
      {showAwardModal && <AwardScholarshipModal />}
      {showRuleModal && <RulesSettingsModal />}
    </div>
  );
};

export default ScholarshipsManager;